import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from 'semantic-ui-react';
import {
  StandaloneChargePaymentMethodEnum,
  StandaloneChargePublicDTO,
  StandaloneChargeStatusEnum
} from '../../services/standalone-charges';
import { successNotification } from '../../services/notification';
import logoLadoBom from '../../resources/imgs/logo-lado-bom-seguros.svg';
import PixPaymentView from './components/PixPaymentView';
import BoletoPaymentView from './components/BoletoPaymentView';
import PaymentStatusView from './components/PaymentStatusView';
import { useStandaloneChargePayment } from './hooks/useStandaloneChargePayment';
import {
  AmountBlock,
  AmountLabel,
  AmountValue,
  Badge,
  CardSection,
  CenteredLoader,
  DescriptionText,
  ErrorBox,
  MainCard,
  MetaRow,
  PageShell,
  PayerName,
  TitleRow,
  TopBar,
  BrandLogo
} from './styles';

const formatCurrency = (amount: number) =>
  `R$ ${Number(amount || 0).toFixed(2).replace('.', ',')}`;

const paymentMethodLabel = (method: StandaloneChargePaymentMethodEnum) =>
  method === StandaloneChargePaymentMethodEnum.pix ? 'Pix' : 'Boleto';

const statusBadge = (charge: StandaloneChargePublicDTO) => {
  const { status } = charge;
  switch (status) {
    case StandaloneChargeStatusEnum.paid:
      return { label: 'Pago', variant: 'success' as const };
    case StandaloneChargeStatusEnum.cancelled:
      return { label: 'Cancelado', variant: 'warning' as const };
    case StandaloneChargeStatusEnum.expired:
      return { label: 'Expirado', variant: 'danger' as const };
    case StandaloneChargeStatusEnum.pendingPayment:
    default:
      return { label: 'Aguardando pagamento', variant: 'default' as const };
  }
};

const StandaloneChargePaymentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { loading, error, charge, isFinished } = useStandaloneChargePayment(token);

  const handleCopy = async (value: string) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    successNotification('Copiado', 'Código copiado com sucesso.');
  };

  return (
    <PageShell>
      <TopBar>
        <BrandLogo src={logoLadoBom} alt="Lado Bom Seguros" />
      </TopBar>

      {loading && (
        <MainCard>
          <CenteredLoader>
            <Loader active size="large" />
            Carregando cobrança...
          </CenteredLoader>
        </MainCard>
      )}

      {!loading && error && <ErrorBox role="alert">{error}</ErrorBox>}

      {!loading && !error && charge && (
        <MainCard>
          <TitleRow>Pagamento da cobrança</TitleRow>

          <AmountBlock>
            <AmountLabel>Valor a pagar</AmountLabel>
            <AmountValue>{formatCurrency(charge.amount)}</AmountValue>
          </AmountBlock>

          {charge.description && (
            <DescriptionText>{charge.description}</DescriptionText>
          )}

          <MetaRow>
            {charge.payerName && (
              <span>
                Pagador: <PayerName>{charge.payerName}</PayerName>
              </span>
            )}
            <span>Forma: {paymentMethodLabel(charge.paymentMethod)}</span>
            <Badge $variant={statusBadge(charge).variant}>
              {statusBadge(charge).label}
            </Badge>
          </MetaRow>

          <CardSection>
            {isFinished ? (
              <PaymentStatusView status={charge.status} paidAt={charge.paidAt} />
            ) : charge.paymentMethod === StandaloneChargePaymentMethodEnum.pix ? (
              <PixPaymentView
                pixQrCode={charge.pixQrCode}
                pixQrCodeUrl={charge.pixQrCodeUrl}
                pixCopyPaste={charge.pixCopyPaste}
                expiresAt={charge.pixExpiresAt || charge.expiresAt}
                onCopy={handleCopy}
              />
            ) : (
              <BoletoPaymentView
                boletoLine={charge.boletoLine}
                boletoPdfUrl={charge.boletoPdfUrl}
                boletoDueAt={charge.boletoDueAt || charge.expiresAt}
                onCopy={handleCopy}
              />
            )}
          </CardSection>
        </MainCard>
      )}
    </PageShell>
  );
};

export default StandaloneChargePaymentPage;
